import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import TextField from "@material-ui/core/TextField";
import { makeStyles } from "@material-ui/core/styles";
import Rating from "@material-ui/lab/Rating";
import { successToastify } from "../../utils/Toastify";
import {
  openFeedbackModal,
  closeFeedbackModal,
  feedbackSubmitSuccess,
  feedbackSubmitError,
} from "../../../../store/actions/feedbackActions";
import SignupAlert from "../Alert/SignupAlert";
import { submitCustomerFeedback } from "../Mutation/Feedback/FeedbackMutation";

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    borderRadius: 8,
    minWidth: 400,
    padding: theme.spacing(2),
  },
  title: {
    fontWeight: 600,
    fontSize: 20,
    color: "#010A43",
    paddingRight: 32,
  },
  closeButton: {
    position: "absolute",
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500],
  },
  rating: {
    margin: theme.spacing(2, 0, 1, 0),
  },
  comment: {
    margin: theme.spacing(1, 0, 2, 0),
  },
}));

const FEEDBACK_MODAL_ENABLED =
  process.env.REACT_APP_ENABLE_FEEDBACK_MODAL === "true";

const FEEDBACK_REPEAT_DELAY = 5 * 60 * 1000; // 5 minutes
const FEEDBACK_BLOCK_DURATION = 24 * 60 * 60 * 1000; // 24 hours
const FEEDBACK_HIDE_MESSAGE_TIMEOUT = 3500; // 3.5 seconds

function RealCustomerFeedbackManager() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const feedback = useSelector((state) => state.feedbackModal);
  const auth = useSelector((state) => state.auth);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showAlert, setShowAlert] = useState(false);

  // Show success toast and only reset showSuccess/showError, not submitted/lastSubmitted
  useEffect(() => {
    if (feedback.showSuccess) {
      successToastify("Thank you for your feedback!");
      setTimeout(() => {
        dispatch({ type: "FEEDBACK_HIDE_MESSAGES" });
      }, FEEDBACK_HIDE_MESSAGE_TIMEOUT);
    }
  }, [feedback.showSuccess, dispatch]);

  // Hide error alert after a while
  useEffect(() => {
    if (feedback.showError) {
      setTimeout(() => {
        dispatch({ type: "FEEDBACK_HIDE_MESSAGES" });
      }, FEEDBACK_HIDE_MESSAGE_TIMEOUT);
    }
  }, [feedback.showError, dispatch]);

  // Expose a global function for meaningful actions (optional, or use Redux directly)
  useEffect(() => {
    window.triggerFeedbackModal = () => {
      dispatch({ type: "FEEDBACK_OPEN_MODAL" });
    };
    return () => {
      delete window.triggerFeedbackModal;
    };
  }, [dispatch]);

  // Only show modal if not submitted in last 24 hours
  useEffect(() => {
    if (
      auth.isAuthenticated &&
      !feedback.submitted &&
      (!feedback.lastSubmitted ||
        Date.now() - feedback.lastSubmitted > FEEDBACK_BLOCK_DURATION)
    ) {
      const timer = setTimeout(() => {
        dispatch(openFeedbackModal());
      }, FEEDBACK_REPEAT_DELAY);
      return () => clearTimeout(timer);
    }
  }, [
    auth.isAuthenticated,
    feedback.submitted,
    feedback.lastSubmitted,
    dispatch,
  ]);

  // Show again after 5 min if closed once, but not after 2 closes or after submit
  useEffect(() => {
    if (
      feedback.closeCount === 1 &&
      !feedback.submitted &&
      (!feedback.lastSubmitted ||
        Date.now() - feedback.lastSubmitted > FEEDBACK_BLOCK_DURATION)
    ) {
      const timer = setTimeout(() => {
        dispatch(openFeedbackModal());
      }, FEEDBACK_REPEAT_DELAY);
      return () => clearTimeout(timer);
    }
  }, [
    feedback.closeCount,
    feedback.submitted,
    feedback.lastSubmitted,
    dispatch,
  ]);

  // If submitted, don't show for 24 hours
  useEffect(() => {
    if (
      feedback.lastSubmitted &&
      Date.now() - feedback.lastSubmitted < FEEDBACK_BLOCK_DURATION
    ) {
      dispatch(closeFeedbackModal());
    }
  }, [feedback.lastSubmitted, dispatch]);

  // Handler for closing modal
  const handleClose = () => {
    dispatch(closeFeedbackModal());
    setShowAlert(false);
  };

  // Handler for submitting feedback
  const handleSubmit = async () => {
    if (!rating) {
      setShowAlert(true);
      return;
    }
    setSubmitting(true);
    setShowAlert(false);
    try {
      const user = auth.user || {};
      const account = user.account || {};
      await submitCustomerFeedback({
        organizationName: account.name,
        organizationId: account.id,
        userName: `${user.firstName || ""} ${user.lastName || ""}`.trim(),
        userId: user.id,
        starRating: rating,
        comment,
      });
      dispatch(feedbackSubmitSuccess());
      setRating(0);
      setComment("");
    } catch (err) {
      dispatch(
        feedbackSubmitError(
          "We couldn't save your feedback. Please try again later."
        )
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={feedback.isOpen}
      onClose={handleClose}
      classes={{ paper: classes.dialogPaper }}
      aria-labelledby="customer-feedback-title"
    >
      <DialogTitle disableTypography id="customer-feedback-title">
        <span className={classes.title}>We value your feedback</span>
        <IconButton className={classes.closeButton} onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <div style={{ textAlign: "center" }}>
          <div className={classes.rating}>
            <Rating
              name="customer-feedback-rating"
              value={rating}
              onChange={(_, value) => setRating(value)}
              size="large"
            />
          </div>
          <TextField
            className={classes.comment}
            label="Additional comments (optional)"
            multiline
            minRows={3}
            fullWidth
            variant="outlined"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            inputProps={{ maxLength: 300 }}
          />
          {showAlert && (
            <SignupAlert
              open={showAlert}
              close={() => setShowAlert(false)}
              message="Please select a star rating."
            />
          )}
          {feedback.showError && (
            <SignupAlert
              open={feedback.showError}
              close={handleClose}
              message={feedback.error}
            />
          )}
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disabled={submitting}
        >
          {submitting ? "Submitting..." : "Submit"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const CustomerFeedbackManager = FEEDBACK_MODAL_ENABLED
  ? RealCustomerFeedbackManager
  : () => null;

export default CustomerFeedbackManager;
